import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import { loadFonts } from "./plugins/webfontloader";
import VueGoogleMaps from "@fawmi/vue-google-maps";

// import bootstrap
import "bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "normalize.css";

// jquery (إن لزم، يمكن الاستيراد عبر npm)
// import $ from 'jquery'


// import firebase 
import './firebase'


// import fontawesome
import { library, dom } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
library.add(fas, far, fab);
dom.watch();

import Swal from 'sweetalert2';

// import axios
import axios from 'axios';
import { resolveAsset } from './utils/assets';
import { initDefaults, fullLogoutWipe, startGlobalRouteLoading, endGlobalRouteLoading, trackNavigationRequest, untrackNavigationRequest, getAuthToken, migrateLegacyAuthToken, broadcastAuthState } from './utils/defaults';
import { installAccessibilityGuards } from './utils/accessibility';
// ضمان استخدام السيرفر المحلي أثناء التطوير حتى إن فشل تحميل .env
// المسار الصحيح يتضمن بادئة "dashboard" بحسب إعدادات الباكند
// استخدم مسارًا نسبيًا ليمر عبر بروكسي devServer أثناء التطوير
axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL || '/dashboard/api/';

const clearAuthToken = () => {
  fullLogoutWipe(axios);
};
const token = migrateLegacyAuthToken(axios) || getAuthToken();
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}
axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
// axios.defaults.headers.lang = sessionStorage.getItem('lang');

// axios.defaults.headers.lang = sessionStorage.getItem('lang');

// إعداد اعتراضات Axios العامة لتوحيد السلوك
axios.interceptors.request.use(
  (config) => {
    trackNavigationRequest(config);
    // Check global maintenance/login status before sending request
    const loginDisabled = localStorage.getItem('login_disabled') === 'true';
    const maintenanceEnabled = localStorage.getItem('maintenance_enabled') === 'true';
    const t = getAuthToken();

    // If user is logged in AND (maintenance is on OR login is disabled)
    // AND the request is not for the maintenance check itself
    if (t && (loginDisabled || maintenanceEnabled) && !config.url.includes('maintenance')) {
        try { untrackNavigationRequest(config); } catch (_) { void 0; }
        // Cancel the request
        const controller = new AbortController();
        config.signal = controller.signal;
        controller.abort();
        
        // Force logout
        localStorage.removeItem('user');
        clearAuthToken();
        router.push('/').catch(() => {});
        
        // Return a rejected promise with a specific reason
        return Promise.reject(new axios.Cancel('Login disabled by administrator'));
    }

    // حدّث التوكن لكل طلب بدلاً من الاعتماد على التهيئة المبدئية فقط
    if (t) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${t}`;
    } else if (config.headers && config.headers['Authorization']) {
      delete config.headers['Authorization'];
    }

    // أضف اللغة إن وُجدت
    const lang = sessionStorage.getItem('lang') || localStorage.getItem('lang');
    if (lang) {
      config.headers = config.headers || {};
      config.headers['lang'] = lang;
    }
    if (typeof config.url === 'string') {
      const urlStr = String(config.url);
      // Rewrite any '/api/...' (relative or absolute) to '/dashboard/api/...'
      const rewritten = urlStr.replace(/^(https?:\/\/[^/]+)?\/api\//, (m, host) => (host ? host : '') + '/dashboard/api/');
      if (rewritten !== urlStr) config.url = rewritten;
      // Special case: shorthand 'maintenance' path without baseURL
      const base = String(config.baseURL || '');
      if (config.url === 'maintenance' && !base.includes('/dashboard/api')) {
        config.url = '/dashboard/api/maintenance';
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => {
    try { untrackNavigationRequest(response?.config); } catch (_) { void 0; }
    return response;
  },
  (error) => {
    try { untrackNavigationRequest(error?.config); } catch (_) { void 0; }
    const status = error?.response?.status;
    const cfg = error?.config || {};
    const skipRedirect = cfg.headers && (cfg.headers['X-No-Auth-Redirect'] === true || cfg.headers['X-No-Auth-Redirect'] === 'true');
    if (status === 401 || status === 403) {
      // إن كان الطلب عامًا وموسومًا لتعطيل إعادة التوجيه، تجاوز النقل
      if (!skipRedirect) {
        const t = getAuthToken();
        if (t) {
          if (!window.__logoutInProgress) {
            window.__logoutInProgress = true;
            clearAuthToken();
            try {
              Swal.fire({
                icon: "warning",
                title: "تم تسجيل الخروج",
                confirmButtonText: "حسناً",
                allowOutsideClick: false,
                backdrop: true,
              }).finally(() => {
                router.push('/').catch(() => null);
                setTimeout(() => { window.__logoutInProgress = false; }, 1500);
              });
            } catch (_) {
              router.push('/').catch(() => null);
              setTimeout(() => { window.__logoutInProgress = false; }, 1500);
            }
          } else {
            router.push('/').catch(() => null);
          }
          return Promise.reject(error);
        }
        // استخدم الراوتر المستورد مباشرة وتجاهل أخطاء الملاحة المكررة
        router.push('/').catch(() => null);
      }
    }
    // قلّل الضوضاء في الـ Console
    // أبقِ الخطأ مرفوضًا ليتم التعامل معه في المكونات عند الحاجة
    return Promise.reject(error);
  }
);

// import sweat alert 
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';



// import pagination
import Paginate from "vuejs-paginate-next";


// import i18n
import i18n from "./i18n";

// import aos scroll animation
// import AOS from 'aos'
import 'aos/dist/aos.css'


loadFonts();
loadFonts()

// Keep focus out of hidden content when overlays set aria-hidden on #app
installAccessibilityGuards();

const app = createApp(App);
app.config.globalProperties.$resolveAsset = resolveAsset;

router.beforeEach((to, from, next) => {
  startGlobalRouteLoading('جاري التحميل...');
  next();
});

router.afterEach(() => {
  setTimeout(() => {
    endGlobalRouteLoading();
  }, 120);
});

router.onError(() => {
  endGlobalRouteLoading();
});

app
  .use(router)
  .use(store)
  .use(i18n)
  .use(vuetify)
  .use(VueSweetalert2)
  .component('Paginate', Paginate)
  // .use(AOS)
  .component("font-awesome-icon", FontAwesomeIcon)
  .use(VueGoogleMaps, {
    load: {
      key: "AIzaSyBNLoYGrbnQI_GMqHt6m0PSN9yA7Zvq7gA",
      // language: 'de',

    },

  })
  .mount("#app");

// تهيئة القيم الافتراضية (مثل صورة المستخدم الافتراضية) بشكل غير متزامن
initDefaults(app);
app.config.globalProperties.$axios = axios;

// مزامنة الحالة بين التبويبات: عند تسجيل دخول/خروج من تبويب آخر
// يتغير التوكن في localStorage فيُحفظ نسخة في sessionStorage هذا التبويب
window.addEventListener('storage', (e) => {
  if (e.key !== 'token') return;
  const t = e.newValue;
  try {
    if (t) {
      sessionStorage.setItem('token', t);
    } else {
      sessionStorage.removeItem('token');
    }
  } catch (_) { void 0; }
  broadcastAuthState(t ? 'update' : 'logout', { hasToken: !!t });
});

(function(){
  const ar = '٠١٢٣٤٥٦٧٨٩';
  const fa = '۰۱۲۳۴۵۶۷۸۹';
  const normalize = (v) => {
    if (v == null) return v;
    const s = String(v);
    if (!/[٠-٩۰-۹]/.test(s)) return s;
    let out = '';
    for (const ch of s) {
      const ai = ar.indexOf(ch);
      if (ai > -1) { out += String(ai); continue; }
      const fi = fa.indexOf(ch);
      if (fi > -1) { out += String(fi); continue; }
      out += ch;
    }
    return out;
  };
  const handler = (e) => {
    const el = e && e.target;
    if (!el) return;
    const tag = String(el.tagName || '').toLowerCase();
    if (tag !== 'input' && tag !== 'textarea') return;
    if (el.readOnly || el.disabled) return;
    const v = el.value;
    if (!v) return;
    const n = normalize(v);
    if (n === v) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    el.value = n;
    if (typeof start === 'number' && typeof end === 'number') {
      const diff = n.length - v.length;
      const ns = Math.max(0, start + diff);
      const ne = Math.max(0, end + diff);
      try { el.setSelectionRange(ns, ne); } catch (_) { void 0; }
    }
  };
  document.addEventListener('input', handler, true);
})();

(function(){
  const targetSuffix = 'images/settings/17651114148619937.png';
  const localSrc = require('@/assets/imgs/last-img.png');
  const apply = (root) => {
    const list = (root && root.querySelectorAll) ? root.querySelectorAll('img') : [];
    for (const img of list) {
      const s = String(img.getAttribute('src') || '').toLowerCase();
      if (s.endsWith(targetSuffix)) img.setAttribute('src', localSrc);
    }
  };
  apply(document);
  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      const nodes = m.addedNodes || [];
      for (const n of nodes) apply(n);
      if (m.type === 'attributes' && m.target && m.attributeName === 'src') {
        const el = m.target;
        if (el && el.tagName && el.tagName.toLowerCase() === 'img') {
          const s = String(el.getAttribute('src') || '').toLowerCase();
          if (s.endsWith(targetSuffix)) el.setAttribute('src', localSrc);
        }
      }
    }
  });
  const root = document.documentElement || document.body;
  if (root) obs.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
})();
